import React, { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import FilterBar from './FilterBar';
import ProductGrid from './ProductGrid';
import { getBrand, getCategory, useBrands } from '../filterConfig';
import { filterProducts } from '../productFilters';
import categorySeo from '../data/categorySeo.json';

export default function CatalogPage({ products, addToCart, onProductClick, wishlistIds, onToggleFav }) {
    const { brandSlug, categorySlug } = useParams();
    const allBrands = useBrands();
    const brand = brandSlug ? getBrand(brandSlug, allBrands) : null;
    const category = categorySlug ? getCategory(categorySlug) : null;

    // SEO: обновляем title, meta description и og-теги
    useEffect(() => {
        let title, description;
        if (brand && category) {
            title = `${category.label} ${brand.label} — купить | Pure Synergy`;
            description = `${category.label} ${brand.label}: каталог с доставкой по РФ. Декларации ЕАС, сертификаты Халяль.`;
        } else if (brand) {
            title = brand.seoTitle;
            description = brand.seoDescription;
        } else if (category) {
            title = category.seoTitle;
            description = category.seoDescription;
        } else {
            title = 'Каталог косметики | Pure Synergy';
            description = 'Профессиональная космецевтика с доказанными активами. Доставка по РФ.';
        }
        document.title = title;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute('content', description);
        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle) ogTitle.setAttribute('content', title);
        const ogDesc = document.querySelector('meta[property="og:description"]');
        if (ogDesc) ogDesc.setAttribute('content', description);
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) {
            canonical.setAttribute('href', `https://puresynergy.ru${window.location.pathname}`);
        }
    }, [brand, category]);

    // Фильтрация товаров
    const filtered = useMemo(
        () =>
            filterProducts(products, {
                brandLabel: brand?.label || '',
                categoryLabel: category?.label || '',
            }),
        [products, brand, category]
    );

    // Хлебные крошки
    const crumbs = [{ to: '/', label: 'Каталог' }];
    if (brand) crumbs.push({ to: `/brand/${brand.slug}`, label: brand.label });
    if (category && brand) {
        crumbs.push({ to: `/brand/${brand.slug}/${category.slug}`, label: category.label });
    } else if (category) {
        crumbs.push({ to: `/category/${category.slug}`, label: category.label });
    }

    // H1 и intro
    let h1 = 'Каталог косметики';
    let intro = null;
    if (brand && category) {
        h1 = `${category.label} ${brand.label}`;
        intro = `${category.intro} От бренда ${brand.label}.`;
    } else if (brand) {
        h1 = brand.h1;
        intro = brand.intro;
    } else if (category) {
        h1 = category.h1;
        intro = category.intro;
    }

    // НОВОЕ: SEO-блоки и FAQ из единого JSON (показываем только если выбрана категория)
    const seo = category && categorySeo[category.slug] ? categorySeo[category.slug] : null;
    const seoBlocks = useMemo(() => seo?.blocks || [], [seo]);
    const faq = useMemo(() => seo?.faq || [], [seo]);

    // НОВОЕ: FAQPage Schema — ставим в head при клиентском роутинге
    useEffect(() => {
        if (!faq.length) return;
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-faq-schema', 'true');
        script.textContent = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faq.map((it) => ({
                '@type': 'Question',
                name: it.q,
                acceptedAnswer: { '@type': 'Answer', text: it.a },
            })),
        });
        document.head.appendChild(script);
        return () => {
            document.head.removeChild(script);
        };
    }, [faq]);

    return (
        <div className="max-w-6xl mx-auto py-8">
            {/* Хлебные крошки */}
            <nav className="text-xs text-gray-500 mb-4 flex flex-wrap gap-1 items-center">
                {crumbs.map((c, i) => (
                    <React.Fragment key={c.to}>
                        {i > 0 && <span className="mx-1">/</span>}
                        {i < crumbs.length - 1 ? (
                            <Link to={c.to} className="hover:text-[#6B7A6A] hover:underline">
                                {c.label}
                            </Link>
                        ) : (
                            <span className="text-[#1C1917]">{c.label}</span>
                        )}
                    </React.Fragment>
                ))}
            </nav>

            <h1 className="serif text-3xl md:text-4xl text-[#1C1917] mb-3">{h1}</h1>
            {intro && <p className="text-gray-600 mb-6 max-w-3xl leading-relaxed">{intro}</p>}

            <FilterBar />

            <div className="flex items-center justify-between mt-6 mb-4">
                <span className="text-sm text-gray-500">Найдено: {filtered.length} товаров</span>
            </div>

            <ProductGrid
                products={filtered}
                addToCart={addToCart}
                onProductClick={onProductClick}
                wishlistIds={wishlistIds}
                onToggleFav={onToggleFav}
            />

            {/* === НОВОЕ: SEO-текст по категории (виден пользователю) === */}
            {seoBlocks.length > 0 && (
                <section className="mt-16 max-w-3xl mx-auto">
                    <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                        {seoBlocks.map((b, i) => {
                            if (b.t === 'h3') {
                                return (
                                    <h3 key={i} className="serif text-2xl text-[#1C1917] mt-8 mb-4">
                                        {b.x}
                                    </h3>
                                );
                            }
                            if (b.t === 'ul') {
                                return (
                                    <ul key={i} className="list-disc pl-6 space-y-2 my-4">
                                        {b.x.map((li, j) => (
                                            <li key={j}>{li}</li>
                                        ))}
                                    </ul>
                                );
                            }
                            return (
                                <p key={i} className="my-4">
                                    {b.x}
                                </p>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* === НОВОЕ: FAQ-блок (виден пользователю, совпадает со Schema) === */}
            {faq.length > 0 && (
                <section className="mt-16 max-w-3xl mx-auto">
                    <h2 className="serif text-2xl md:text-3xl text-[#1C1917] mb-6 text-center">
                        Частые вопросы
                    </h2>
                    <div className="space-y-3">
                        {faq.map((item, i) => (
                            <details
                                key={i}
                                className="bg-white rounded-2xl border border-[#E8E3DD] p-5 group"
                            >
                                <summary className="font-semibold text-[#1C1917] cursor-pointer list-none flex items-start justify-between gap-3">
                                    <span>{item.q}</span>
                                    <span className="text-[#6B7A6A] text-xl leading-none transition-transform group-open:rotate-45 shrink-0">
                                        +
                                    </span>
                                </summary>
                                <p className="text-gray-700 leading-relaxed mt-3">{item.a}</p>
                            </details>
                        ))}
                    </div>
                </section>
            )}

            {/* === НОВОЕ: CTA-блок AI-консультанта === */}
            {(seoBlocks.length > 0 || faq.length > 0) && (
                <section className="mt-16 max-w-3xl mx-auto bg-gradient-to-br from-[#6B7A6A] to-[#4A5A4A] rounded-3xl p-8 text-white text-center">
                    <p className="serif text-2xl mb-3">Не нашли ответ?</p>
                    <p className="text-gray-200 mb-6 text-sm max-w-xl mx-auto">
                        AI-консультант подберёт уход по типу кожи, задаче и бюджету за 1 минуту.
                    </p>
                    <button
                        onClick={() => window.dispatchEvent(new CustomEvent('open-chatbot'))}
                        className="bg-white text-[#6B7A6A] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition shadow-lg inline-flex items-center gap-2"
                    >
                        <span>🤖</span> Спросить AI-консультанта
                    </button>
                </section>
            )}

            {/* Ссылки на другие бренды/категории — помогают SEO и юзабилити */}
            <div className="mt-16 pt-8 border-t border-[#E8E3DD]">
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">
                    Смотрите также
                </p>
                <div className="flex flex-wrap gap-3">
                    {brand ? (
                        <>
                            {['syvorotki', 'uvlazhnyayushchie', 'ochishchayushchie', 'spf', 'nabory'].map((slug) => {
                                const c = getCategory(slug);
                                if (!c) return null;
                                return (
                                    <Link
                                        key={slug}
                                        to={`/brand/${brand.slug}/${slug}`}
                                        className="px-4 py-2 rounded-full bg-[#F5F0EB] text-sm text-[#1C1917] hover:bg-[#E8E3DD] transition"
                                    >
                                        {c.label} {brand.label}
                                    </Link>
                                );
                            })}
                        </>
                    ) : category ? (
                        <>
                            {allBrands
                                .filter((x) => x.slug !== brandSlug)
                                .slice(0, 3)
                                .map((b) => (
                                    <Link
                                        key={b.slug}
                                        to={`/brand/${b.slug}/${category.slug}`}
                                        className="px-4 py-2 rounded-full bg-[#F5F0EB] text-sm text-[#1C1917] hover:bg-[#E8E3DD] transition"
                                    >
                                        {category.label} {b.label}
                                    </Link>
                                ))}
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
       
    
